import type { GlCode } from '@/types/models';

/**
 * GL-code hierarchy helpers. The Chart of Accounts is a tree driven entirely by
 * the dotted `code` string ("server-style" multi-dot codes):
 *
 *   7000          ADMINISTRATIVE      (root — no dot)
 *     7000.01     Copies              (child of 7000)
 *     7000.02     Postage
 *       7000.02.01  Certified mail    (grandchild of 7000.02)
 *
 * The code is the single source of truth: a node's parent is its code with the
 * last `.`-segment removed, and depth is the number of `.` segments. `parent_id`
 * and `full_path` are derived from this on the server (see api/gl-codes.ts).
 *
 * This module is intentionally RN-free so it can be unit-tested in plain Vitest.
 */

/** Parent code of a dotted code, or null for a root (no dot). e.g. "7000.02.01" -> "7000.02". */
export const parentCodeOf = (code: string): string | null => {
	const i = code.lastIndexOf('.');
	return i === -1 ? null : code.slice(0, i);
};

/**
 * Top-level group key — the first two digits of a code's integer part. By
 * convention the leading pair buckets accounts into a section (10 = Bank
 * Accounts, 12 = A/R, 70 = Operating Expenses, 80 = Capital, …). "7060.40" and
 * "7095" both map to "70". Returns '' for a code with no leading digits.
 */
export const groupKeyOf = (code: string): string => code.split('.')[0].replace(/\D.*$/, '').slice(0, 2);

/**
 * Display names for the default chart's top-level groups. Unknown prefixes fall
 * back to the bare key (see groupLabel) so customized/imported charts still render.
 */
export const DEFAULT_GROUP_LABELS: Record<string, string> = {
	'09': 'Prior Management Funds',
	'10': 'Bank Accounts',
	'12': 'Accounts Receivable',
	'14': 'Other Current Assets',
	'15': 'Fixed Assets',
	'20': 'Accounts Payable',
	'21': 'Other Current Liabilities',
	'25': 'Long-Term Liabilities',
	'28': 'Fund Balances & Equity',
	'30': 'Income',
	'31': 'Fees',
	'32': 'Interest',
	'70': 'Operating Expenses',
	'71': 'Community & Events',
	'79': 'Other Operating Expenses',
	'80': 'Capital Expenditures',
	'81': 'Capital — Systems & Reserves',
};

/** Human label for a group key, falling back to the key itself for unknown groups. */
export const groupLabel = (key: string): string => DEFAULT_GROUP_LABELS[key] ?? key;

/** Nesting depth: number of `.` segments. Root = 0, "7000.01" = 1, "7000.01.02" = 2. */
export const depthOf = (code: string): number => {
	let n = 0;
	for (let i = 0; i < code.length; i++) if (code[i] === '.') n++;
	return n;
};

/**
 * Comparator for two GL codes that orders them the way an accountant expects:
 * segment-by-segment, NUMERICALLY when both segments are numbers (so "9" < "10"),
 * falling back to string compare otherwise. A shorter prefix sorts before a
 * longer one that extends it ("7000" before "7000.01").
 *
 * Examples: "9000" < "10000", "7000.2" < "7000.10", "7000" < "7000.01".
 */
export const compareGlCodes = (a: string, b: string): number => {
	const as = a.split('.');
	const bs = b.split('.');
	const len = Math.min(as.length, bs.length);
	for (let i = 0; i < len; i++) {
		const an = Number(as[i]);
		const bn = Number(bs[i]);
		// Compare numerically only when BOTH segments are real numbers, so "9" < "10";
		// otherwise fall back to string compare (e.g. "7000.A" vs "7000.B").
		const cmp = Number.isFinite(an) && Number.isFinite(bn) && as[i] !== '' && bs[i] !== ''
			? an - bn
			: as[i].localeCompare(bs[i]);
		if (cmp !== 0) return cmp < 0 ? -1 : 1;
	}
	// All shared segments equal → the shorter code (a prefix, i.e. the parent) sorts first.
	return as.length - bs.length;
};

export interface GlNode {
	code: GlCode;
	children: GlNode[];
	depth: number;
}

/**
 * Build the GL hierarchy from a flat list. A node nests under its EXPLICIT
 * `parentId` (server-set from the seed CSV's `parentCode` or derived from a
 * dotted code) when that parent is present; otherwise it falls back to the
 * dotted-code parent (`parentCodeOf`). This lets a flat sub-account code like
 * `1051` nest under `1050` (parentId) while a chart with no `parent_id`
 * populated still nests purely by its dotted codes. Rows whose parent is absent
 * from the set (orphans, e.g. a hidden/renumbered parent) attach as roots so
 * nothing silently disappears. `depth` is the distance from the root, and roots
 * + every sibling list are sorted with `compareGlCodes`.
 */
export const buildGlTree = (codes: GlCode[]): { roots: GlNode[] } => {
	const byId = new Map<string, GlNode>();
	const byCode = new Map<string, GlNode>();
	for (const c of codes) {
		const node: GlNode = { code: c, children: [], depth: 0 };
		byId.set(c.id, node);
		byCode.set(c.code, node);
	}
	const parentOf = (node: GlNode): GlNode | undefined => {
		const byParentId = node.code.parentId ? byId.get(node.code.parentId) : undefined;
		if (byParentId) return byParentId;
		const parentCode = parentCodeOf(node.code.code);
		return parentCode != null ? byCode.get(parentCode) : undefined;
	};
	const roots: GlNode[] = [];
	for (const node of byId.values()) {
		const parent = parentOf(node);
		if (parent && parent !== node) parent.children.push(node);
		else roots.push(node);
	}
	// Depth = distance from root (a flat sub-account has no dots, so we can't use
	// the dot count — walk the built tree instead).
	const setDepth = (list: GlNode[], d: number) => {
		for (const n of list) { n.depth = d; setDepth(n.children, d + 1); }
	};
	setDepth(roots, 0);
	const sortRec = (list: GlNode[]) => {
		list.sort((x, y) => compareGlCodes(x.code.code, y.code.code));
		for (const n of list) sortRec(n.children);
	};
	sortRec(roots);
	return { roots };
};
